import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtCount, fmtPct, statusColor } from '../components/ContactCenter/format';
import { btn, c, card, tbl } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · Staffing — planned against actual, per pod, with
// occupancy, adherence and shrinkage.
//
// Two honesty rules the old page did not have. A past day nobody sampled
// reports "not sampled" rather than zero on shift: Genesys keeps no history
// of presence, so an unwatched day is unknown, not empty. And shrinkage is
// measured against scheduled time elapsed SO FAR, so a morning reads as a
// morning rather than as a catastrophe.

const pacificToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
const addDays = (day, n) => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// The old page's bands.
const occupancyTone = (v) => (v == null ? null : v >= 90 ? 'watch' : v >= 75 ? 'good' : 'bad');
const adherenceTone = (v) => (v == null ? null : v >= 90 ? 'good' : v >= 85 ? 'watch' : 'bad');
const varianceTone = (v) => (v == null ? null : v <= -3 ? 'bad' : v < 0 ? 'watch' : 'good');
const shrinkTone = (v) => (v == null ? null : v >= 20 ? 'bad' : v >= 13 ? 'watch' : 'good');

const SHRINK_LABELS = [['breaks', 'Breaks'], ['lunch', 'Lunch'], ['training', 'Training'],
  ['meetings', 'Meetings'], ['unplanned', 'Unplanned'], ['other', 'Other']];
const fmtHm = (secs) => (secs == null ? '—' : secs >= 3600
  ? `${Math.floor(secs / 3600)}h ${String(Math.floor((secs % 3600) / 60)).padStart(2, '0')}m`
  : `${Math.floor(secs / 60)}m`);

export default function ContactCenterStaffing() {
  const today = pacificToday();
  const [day, setDay] = useState(today);

  const q = useQuery({
    queryKey: ['cc-staffing', day],
    queryFn: () => api.get('/contact-center/staffing', { params: { day } }),
    refetchInterval: day === today ? 60 * 1000 : false,
  });
  const d = q.data;
  const cell = (value, tone) => <td style={{ ...s.num, color: statusColor(tone), fontWeight: tone ? 600 : 400 }}>{value}</td>;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Contact Center"
        title="Staffing"
        subtitle={d ? `${d.live ? 'Now' : d.day} · planned against actual` : 'Planned against actual'}
        actions={(
          <div style={s.row} role="group" aria-label="Day">
            <button type="button" onClick={() => setDay(today)} aria-pressed={day === today} style={day === today ? s.pillOn : s.pill}>Today</button>
            <button type="button" onClick={() => setDay(addDays(today, -1))} aria-pressed={day === addDays(today, -1)}
              style={day === addDays(today, -1) ? s.pillOn : s.pill}>Yesterday</button>
            <input id="cc-staffing-day" type="date" aria-label="Day" max={today} value={day}
              onChange={(e) => e.target.value && setDay(e.target.value)} style={s.input} />
          </div>
        )}
        alignActions="end"
      />

      {q.isLoading && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load Staffing: {q.error.message}</div>}

      {d && !d.hasSchedule && (
        <p style={s.notice}>
          No published WFM schedule for {d.day}, so there is nothing to compare against. On shift and occupancy are still shown.
        </p>
      )}
      {d && !d.live && !d.sampled && (
        <p style={s.notice}>
          Nobody was sampling the floor on {d.day}, so how many were on shift is unknown rather than zero. Days from
          now on carry it.
        </p>
      )}

      {d && (
        <section style={s.card} aria-label="Planned against actual">
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Pod</th><th style={s.thNum}>Planned</th>
                  <th style={s.thNum}>{d.live ? 'On shift' : 'Peak on shift'}</th>
                  <th style={s.thNum}>Variance</th><th style={s.thNum}>Occupancy</th>
                  <th style={s.thNum}>Adherence</th><th style={s.thNum}>Shrinkage</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.pod} style={s.tr}>
                    <td style={s.td}>{r.pod}</td>
                    {cell(r.planned == null ? '—' : fmtCount(r.planned))}
                    {cell(r.onShift == null ? '—' : fmtCount(r.onShift))}
                    {cell(r.variance == null ? '—' : r.variance > 0 ? `+${r.variance}` : r.variance, varianceTone(r.variance))}
                    {cell(fmtPct(r.occupancy), occupancyTone(r.occupancy))}
                    {cell(r.adherence == null ? '—' : `${r.adherence}%`, adherenceTone(r.adherence))}
                    {cell(fmtPct(r.shrinkage.shrinkPct), shrinkTone(r.shrinkage.shrinkPct))}
                  </tr>
                ))}
                <tr style={s.totalRow}>
                  <td style={{ ...s.td, fontWeight: 650 }}>All pods</td>
                  {cell(d.totals.planned == null ? '—' : fmtCount(d.totals.planned))}
                  {cell(d.totals.onShift == null ? '—' : fmtCount(d.totals.onShift))}
                  {cell(d.totals.variance == null ? '—' : d.totals.variance > 0 ? `+${d.totals.variance}` : d.totals.variance,
                    varianceTone(d.totals.variance))}
                  {cell(fmtPct(d.totals.occupancy), occupancyTone(d.totals.occupancy))}
                  {cell(d.totals.adherence == null ? '—' : `${d.totals.adherence}%`, adherenceTone(d.totals.adherence))}
                  {cell(fmtPct(d.totals.shrinkage.shrinkPct), shrinkTone(d.totals.shrinkage.shrinkPct))}
                </tr>
              </tbody>
            </table>
          </div>
          <p style={s.foot}>
            Planned counts agents whose published schedule has them working at this moment. Occupancy is handling time over
            On Queue time; adherence is {d.live
              ? 'the share of scheduled agents Genesys counts in adherence now'
              : 'the day’s figures, weighted by scheduled time'}.
          </p>
        </section>
      )}

      {d && (
        <section style={s.card} aria-label="Shrinkage">
          <h2 style={s.h2}>Shrinkage · {d.live ? 'so far today' : d.day}</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Pod</th><th style={s.thNum}>Scheduled</th><th style={s.thNum}>Total shrink</th>
                  {SHRINK_LABELS.map(([k, label]) => <th key={k} style={s.thNum}>{label}</th>)}
                  <th style={s.thNum}>Time off</th><th style={s.thNum}>Planned off</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.pod} style={s.tr}>
                    <td style={s.td}>{r.pod}</td>
                    <td style={s.num}>{fmtHm(r.shrinkage.scheduledSecs)}</td>
                    {cell(fmtPct(r.shrinkage.shrinkPct), shrinkTone(r.shrinkage.shrinkPct))}
                    {SHRINK_LABELS.map(([k]) => (
                      <td key={k} style={s.num}>{fmtPct(r.shrinkage.shares[k])}</td>
                    ))}
                    <td style={s.num}>{fmtHm(r.shrinkage.buckets.timeOff)}</td>
                    <td style={s.num} title="Booked, planned absence — Vacation, Jury Duty. Not scheduled time and not shrinkage: as if the agent had no shift that stretch.">
                      {fmtHm(r.shrinkage.plannedOffSecs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={s.foot}>
            Shares are of scheduled time elapsed so far, so a morning reads as a morning. Buckets follow the activity
            code&rsquo;s category, not its name. Time off splits on Genesys&rsquo;s own plannedShrinkage flag: an
            unplanned absence (Sick, FMLA, Late) is scheduled time AND shrinkage, folded into Unplanned above and
            named again under Time off; a planned one (Vacation, Jury Duty) is neither &mdash; Planned off keeps it
            visible without it inflating either figure.
          </p>
        </section>
      )}
    </div>
  );
}

const s = {
  page:     { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  row:      { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  pill:     { ...btn.secondaryXs },
  pillOn:   { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  input:    { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, background: c.surface },
  error:    { fontSize: '13px', color: c.dangerText },
  notice:   { fontSize: '12.5px', color: c.textMuted, background: c.surfaceSunk, padding: '10px 14px', borderRadius: '8px', margin: 0 },
  card:     { ...card.base, padding: '14px 18px' },
  h2:       { fontSize: '13px', fontWeight: 650, color: c.text, margin: '0 0 10px' },
  table:    { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:       { ...tbl.th, textAlign: 'left' },
  thNum:    { ...tbl.thR, whiteSpace: 'nowrap' },
  td:       { ...tbl.td, color: c.textStrong },
  num:      { ...tbl.tdR, color: c.textStrong },
  tr:       { ...tbl.tr },
  totalRow: { ...tbl.tr, borderTop: `2px solid ${c.borderStrong}` },
  foot:     { fontSize: '11px', color: c.textMuted, margin: '8px 0 0', lineHeight: 1.45 },
};
